<template>
  <PopUp
    :is_open="open"
    :title="modalTitle"
    size="lg"
    auto_height
    @onClose="close"
  >
    <template #content>
      <div v-if="success" class="booking-wizard__success">
        <span class="booking-wizard__success-icon" aria-hidden="true">✓</span>
        <div class="booking-wizard__success-body">
          <p class="booking-wizard__success-title">Ihre Buchungsanfrage ist bei uns angekommen</p>
          <p v-if="apartmentName && form.check_in && form.check_out" class="booking-wizard__success-meta">
            <strong>{{ apartmentName }}</strong>
            · {{ formatDateDisplay(form.check_in) }} – {{ formatDateDisplay(form.check_out) }}
          </p>
          <p class="booking-wizard__success-text">
            Wir prüfen den gewünschten Zeitraum und melden uns werktags in der Regel innerhalb von
            24&nbsp;Stunden bei Ihnen — per E-Mail oder telefonisch, wie Sie es uns mitgeteilt haben.
          </p>
        </div>
      </div>

      <div v-else class="booking-wizard">
        <nav
          class="app-modal-wizard__steps app-modal-wizard__steps--4"
          aria-label="Buchungsanfrage Schritte"
        >
          <button
            v-for="step in steps"
            :key="step.id"
            type="button"
            class="app-modal-wizard__step"
            :class="{
              'app-modal-wizard__step--active': currentStep === step.id,
              'app-modal-wizard__step--done': currentStep > step.id,
              'app-modal-wizard__step--clickable': step.id < currentStep
            }"
            :disabled="step.id >= currentStep || submitting"
            @click="goToStep(step.id)"
          >
            <span class="app-modal-wizard__step-index">
              <span v-if="currentStep > step.id" class="app-modal-wizard__step-check" aria-hidden="true">✓</span>
              <span v-else>{{ step.id }}</span>
            </span>
            <span class="app-modal-wizard__step-label">{{ step.label }}</span>
          </button>
        </nav>

        <div
          v-if="currentStep > 1 && form.check_in && form.check_out"
          class="booking-wizard__dates-badge"
        >
          <span class="booking-wizard__dates-label">Zeitraum</span>
          {{ formatDateDisplay(form.check_in) }} – {{ formatDateDisplay(form.check_out) }}
        </div>

        <p class="app-modal-intro">{{ steps[currentStep - 1].intro }}</p>

        <div v-if="error" class="app-modal-alert app-modal-alert--error">{{ error }}</div>

        <Transition name="booking-step" mode="out-in">
          <div :key="currentStep" class="booking-wizard__panel">
        <!-- Schritt 1: Zeitraum -->
        <div v-show="currentStep === 1" class="app-modal-form">
          <ApartmentSelector
            v-if="apartments.length > 1"
            class="booking-wizard__apartment-switcher"
            :apartments="apartments"
            :selectedApartment="selectedApartmentSlug"
            @update:selectedApartment="onApartmentChange"
          />
          <AvailabilityCalendar
            selectable
            :apartment="activeApartment"
            :active="open && currentStep === 1"
            :check-in="form.check_in"
            :check-out="form.check_out"
            @update:check-in="form.check_in = $event"
            @update:check-out="form.check_out = $event"
          />
        </div>

        <!-- Schritt 2: Kontakt -->
        <form v-show="currentStep === 2" class="app-modal-form booking-form" @submit.prevent="goNext">
          <div class="app-modal-form__section">
            <p class="app-modal-form__section-title">Kontaktdaten</p>
            <div class="app-modal-form__grid app-modal-form__grid--2">
              <div class="app-modal-form__field">
                <label for="booking-first-name">Vorname *</label>
                <input
                  id="booking-first-name"
                  v-model="form.first_name"
                  type="text"
                  class="form-control"
                  placeholder="Vorname"
                  autocomplete="given-name"
                />
              </div>
              <div class="app-modal-form__field">
                <label for="booking-last-name">Nachname *</label>
                <input
                  id="booking-last-name"
                  v-model="form.last_name"
                  type="text"
                  class="form-control"
                  placeholder="Nachname"
                  autocomplete="family-name"
                />
              </div>
              <div class="app-modal-form__field">
                <label for="booking-email">E-Mail *</label>
                <input
                  id="booking-email"
                  v-model="form.email"
                  type="email"
                  class="form-control"
                  placeholder="name@beispiel.de"
                  autocomplete="email"
                />
              </div>
              <div class="app-modal-form__field">
                <label for="booking-phone">Telefon *</label>
                <input
                  id="booking-phone"
                  v-model="form.phone"
                  type="tel"
                  class="form-control"
                  placeholder="Telefonnummer"
                  autocomplete="tel"
                />
              </div>
            </div>
          </div>
        </form>

        <!-- Schritt 3: Details -->
        <form v-show="currentStep === 3" class="app-modal-form booking-form" @submit.prevent="goNext">
          <div class="app-modal-form__section">
            <p class="app-modal-form__section-title">Aufenthalt</p>
            <div class="app-modal-form__grid app-modal-form__grid--compact">
              <div class="app-modal-form__field">
                <label for="booking-guest-count">Personen *</label>
                <input
                  id="booking-guest-count"
                  v-model.number="form.guest_count"
                  type="number"
                  min="1"
                  :max="maxGuests || 10"
                  class="form-control"
                />
              </div>
            </div>
          </div>

          <div class="app-modal-form__section">
            <p class="app-modal-form__section-title">
              Adresse
              <span class="app-modal-form__optional">optional</span>
            </p>
            <div class="app-modal-form__grid">
              <div class="app-modal-form__field">
                <label for="booking-street">Straße</label>
                <input
                  id="booking-street"
                  v-model="form.street"
                  type="text"
                  class="form-control"
                  placeholder="Straße und Hausnummer"
                  autocomplete="street-address"
                />
              </div>
              <div class="app-modal-form__grid app-modal-form__grid--split">
                <div class="app-modal-form__field">
                  <label for="booking-postal-code">PLZ</label>
                  <input
                    id="booking-postal-code"
                    v-model="form.postal_code"
                    type="text"
                    class="form-control"
                    placeholder="PLZ"
                    inputmode="numeric"
                    autocomplete="postal-code"
                  />
                </div>
                <div class="app-modal-form__field">
                  <label for="booking-city">Ort</label>
                  <input
                    id="booking-city"
                    v-model="form.city"
                    type="text"
                    class="form-control"
                    placeholder="Ort"
                    autocomplete="address-level2"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="app-modal-form__section">
            <p class="app-modal-form__section-title">
              Nachricht
              <span class="app-modal-form__optional">optional</span>
            </p>
            <div class="app-modal-form__field">
              <textarea
                id="booking-notes"
                v-model="form.notes"
                class="form-control"
                placeholder="Anmerkungen zu Ihrer Anfrage"
                aria-label="Nachricht"
              ></textarea>
            </div>
          </div>
        </form>

        <!-- Schritt 4: Zusammenfassung -->
        <div v-show="currentStep === 4" class="booking-wizard__summary">
          <div class="booking-wizard__summary-card">
            <h4 class="booking-wizard__summary-title">Ihre Anfrage im Überblick</h4>
            <dl class="booking-wizard__summary-list">
              <div class="booking-wizard__summary-item booking-wizard__summary-item--highlight">
                <dt>Zeitraum</dt>
                <dd>
                  {{ formatDateDisplay(form.check_in) }}
                  –
                  {{ formatDateDisplay(form.check_out) }}
                </dd>
              </div>
              <div class="booking-wizard__summary-item">
                <dt>Personen</dt>
                <dd>{{ form.guest_count }}</dd>
              </div>
              <div class="booking-wizard__summary-item">
                <dt>Name</dt>
                <dd>{{ form.first_name }} {{ form.last_name }}</dd>
              </div>
              <div class="booking-wizard__summary-item">
                <dt>E-Mail</dt>
                <dd>{{ form.email }}</dd>
              </div>
              <div class="booking-wizard__summary-item">
                <dt>Telefon</dt>
                <dd>{{ form.phone }}</dd>
              </div>
              <div v-if="hasAddress" class="booking-wizard__summary-item">
                <dt>Adresse</dt>
                <dd>{{ formattedAddress }}</dd>
              </div>
              <div v-if="form.notes?.trim()" class="booking-wizard__summary-item">
                <dt>Nachricht</dt>
                <dd>{{ form.notes }}</dd>
              </div>
            </dl>
          </div>
        </div>
          </div>
        </Transition>

        <form
          v-if="currentStep === CONFIRM_STEP && open && !success"
          :key="captchaKey"
          class="app-modal-form booking-form booking-form--captcha"
          @submit.prevent="submit"
        >
          <p v-if="!captchaValid" class="booking-wizard__captcha-hint">
            Bitte warten Sie, bis die Sicherheitsprüfung abgeschlossen ist.
          </p>
          <FriendlyCaptchaField
            @validated="captchaValid = $event"
            @error="error = $event"
          />
        </form>
      </div>
    </template>

    <template v-if="!success" #footer>
      <div class="booking-wizard__footer">
        <div class="booking-wizard__footer-start">
          <button
            v-if="currentStep > 1"
            type="button"
            class="button dark booking-wizard__btn"
            @click="goBack"
            :disabled="submitting"
          >
            Zurück
          </button>
        </div>
        <div class="booking-wizard__footer-end">
          <button
            v-if="currentStep < CONFIRM_STEP"
            type="button"
            class="button light booking-wizard__btn"
            @click="goNext"
          >
            Weiter
          </button>
          <button
            v-else
            type="button"
            class="button light booking-wizard__btn"
            :disabled="submitting"
            @click="submit"
          >
            {{ submitting ? 'Wird gesendet…' : 'Reservierung anfragen' }}
          </button>
        </div>
      </div>
    </template>
  </PopUp>
</template>

<script>
import { reactive, ref, watch, computed } from 'vue'
import { useApi } from '../../composables/useApi.js'
import { validateBookingRequest } from '../../lib/booking.js'
import { formatDateDisplay } from '../../composables/useAvailabilityCalendar.js'
import PopUp from './PopUp.vue'
import AvailabilityCalendar from './AvailabilityCalendar.vue'
import FriendlyCaptchaField from './FriendlyCaptchaField.vue'
import ApartmentSelector from './ApartmentSelector.vue'
import { trackBookingInquiry } from '../../lib/analytixTracking.js'

const STEPS = [
  {
    id: 1,
    label: 'Zeitraum',
    intro: 'Wählen Sie Anreise und Abreise per Klick im Kalender — freie Tage sind anklickbar, belegte nicht.'
  },
  {
    id: 2,
    label: 'Kontakt',
    intro: 'Wie können wir Sie erreichen?'
  },
  {
    id: 3,
    label: 'Details',
    intro: 'Personenanzahl, optionale Adresse und Anmerkungen zu Ihrer Anfrage.'
  },
  {
    id: 4,
    label: 'Bestätigen',
    intro: 'Prüfen Sie Ihre Angaben und senden Sie die unverbindliche Anfrage ab.'
  }
]

const TOTAL_STEPS = STEPS.length
const CONFIRM_STEP = TOTAL_STEPS

const emptyForm = () => ({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  street: '',
  postal_code: '',
  city: '',
  check_in: '',
  check_out: '',
  guest_count: 2,
  notes: ''
})

export default {
  components: {
    PopUp,
    AvailabilityCalendar,
    FriendlyCaptchaField,
    ApartmentSelector
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    apartment: {
      type: Object,
      default: null
    },
    apartments: {
      type: Array,
      default: () => []
    },
    initialCheckIn: {
      type: String,
      default: ''
    },
    initialCheckOut: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'success', 'update:apartment'],
  setup(props, { emit }) {
    const api = useApi()
    const form = reactive(emptyForm())
    const currentStep = ref(1)
    const submitting = ref(false)
    const error = ref('')
    const success = ref(false)
    const captchaValid = ref(false)
    const captchaKey = ref(0)
    const selectedApartmentSlug = ref('')

    const activeApartment = computed(() => {
      if (!props.apartments?.length) return props.apartment
      return (
        props.apartments.find((item) => item.slug === selectedApartmentSlug.value) ||
        props.apartment
      )
    })

    const syncSelectedApartment = () => {
      selectedApartmentSlug.value = props.apartment?.slug || ''
    }

    const onApartmentChange = (slug) => {
      if (!slug || slug === selectedApartmentSlug.value) return
      selectedApartmentSlug.value = slug
      form.check_in = ''
      form.check_out = ''
      error.value = ''
      if (currentStep.value > 1) {
        currentStep.value = 1
      }
      emit('update:apartment', slug)
    }

    const resetCaptcha = () => {
      captchaValid.value = false
      captchaKey.value += 1
    }

    const steps = STEPS
    const apartmentName = computed(
      () => activeApartment.value?.name || activeApartment.value?.title || ''
    )
    const maxGuests = computed(() => Number(activeApartment.value?.max_guests) || 10)
    const modalTitle = computed(() =>
      apartmentName.value ? `Buchungsanfrage – ${apartmentName.value}` : 'Buchungsanfrage'
    )
    const hasDateRange = computed(() => Boolean(form.check_in && form.check_out))
    const hasAddress = computed(
      () => Boolean(form.street?.trim() || form.postal_code?.trim() || form.city?.trim())
    )
    const formattedAddress = computed(() => {
      const street = form.street?.trim()
      const postalCode = form.postal_code?.trim()
      const city = form.city?.trim()
      const cityLine = [postalCode, city].filter(Boolean).join(' ')
      return [street, cityLine].filter(Boolean).join(', ')
    })

    const resetForm = () => {
      Object.assign(form, emptyForm())
      form.guest_count = Math.min(2, maxGuests.value)
      form.check_in = props.initialCheckIn || ''
      form.check_out = props.initialCheckOut || ''
      currentStep.value = 1
      error.value = ''
      success.value = false
      submitting.value = false
      resetCaptcha()
    }

    const validateStep = (step) => {
      if (step === 1) {
        if (!hasDateRange.value) {
          error.value = 'Bitte wählen Sie Anreise und Abreise im Kalender.'
          return false
        }
        return true
      }

      if (step === 2) {
        const validationErrors = validateBookingRequest(form, activeApartment.value).filter(
          (message) =>
            !message.includes('Anreise') &&
            !message.includes('Abreise') &&
            !message.includes('Personen')
        )
        if (validationErrors.length > 0) {
          error.value = validationErrors[0]
          return false
        }
        return true
      }

      if (step === 3) {
        const validationErrors = validateBookingRequest(form, activeApartment.value).filter(
          (message) =>
            message.includes('Personen') ||
            message.includes('Personen in dieser Ferienwohnung')
        )
        if (validationErrors.length > 0) {
          error.value = validationErrors[0]
          return false
        }
        return true
      }

      return true
    }

    const goNext = async () => {
      error.value = ''
      if (!validateStep(currentStep.value)) return

      if (currentStep.value < CONFIRM_STEP) {
        currentStep.value += 1
      }
    }

    const goBack = () => {
      error.value = ''
      if (currentStep.value > 1) {
        if (currentStep.value === CONFIRM_STEP) {
          captchaValid.value = false
        }
        currentStep.value -= 1
      }
    }

    const goToStep = (stepId) => {
      if (stepId >= currentStep.value || submitting.value) return
      error.value = ''
      if (currentStep.value === CONFIRM_STEP) {
        captchaValid.value = false
      }
      currentStep.value = stepId
    }

    const formatSubmitError = (err) => {
      const message = err?.message || ''
      if (err?.status === 403 || message.includes('Write permission')) {
        return 'Schreibzugriff fehlt — bitte prüfen Sie den API-Key in MyPortal (Write-Berechtigung für haus_birkengruen_db).'
      }
      return message || 'Die Anfrage konnte nicht gesendet werden.'
    }

    watch(
      () => props.open,
      (isOpen) => {
        if (isOpen) {
          syncSelectedApartment()
          resetForm()
          return
        }
        captchaValid.value = false
      }
    )

    watch(
      () => props.apartment?.slug,
      () => {
        if (props.open) {
          syncSelectedApartment()
        }
      }
    )

    watch(
      () => [props.initialCheckIn, props.initialCheckOut],
      ([checkIn, checkOut]) => {
        if (props.open) {
          if (checkIn) form.check_in = checkIn
          if (checkOut) form.check_out = checkOut
        }
      }
    )

    const close = () => {
      emit('close')
    }

    const submit = async () => {
      if (!captchaValid.value) {
        error.value = 'Bitte warten Sie, bis die Sicherheitsprüfung abgeschlossen ist.'
        return
      }

      error.value = ''
      submitting.value = true

      try {
        const result = await api.submitBookingRequest(activeApartment.value, { ...form })
        trackBookingInquiry({
          apartment: activeApartment.value,
          checkIn: form.check_in,
          checkOut: form.check_out,
          guestCount: form.guest_count,
        })
        captchaValid.value = false
        success.value = true
        emit('success', result)
      } catch (err) {
        error.value = formatSubmitError(err)
        resetCaptcha()
      } finally {
        submitting.value = false
      }
    }

    return {
      form,
      steps,
      currentStep,
      submitting,
      error,
      success,
      apartmentName,
      maxGuests,
      modalTitle,
      hasAddress,
      formattedAddress,
      captchaKey,
      captchaValid,
      selectedApartmentSlug,
      activeApartment,
      onApartmentChange,
      formatDateDisplay,
      close,
      goNext,
      goBack,
      goToStep,
      CONFIRM_STEP,
      submit
    }
  }
}
</script>

<style scoped lang="scss">
.booking-wizard__dates-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #f4fcfd 0%, #eefafb 100%);
  border: 1px solid rgba(66, 217, 228, 0.3);
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  color: #2f2f2f;
}

.booking-wizard__dates-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6a8a8f;
}

.booking-wizard__panel {
  min-height: 120px;
}

.booking-step-enter-active,
.booking-step-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.booking-step-enter-from {
  opacity: 0;
  transform: translateX(10px);
}

.booking-step-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.booking-wizard__success {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin: 0;
  padding: 20px;
  background: linear-gradient(135deg, #f6fbf6 0%, #edf7ed 100%);
  border: 1px solid rgba(76, 140, 90, 0.25);
  border-radius: 12px;
  color: #2f6b2f;
  line-height: 1.6;
}

.booking-wizard__success-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.booking-wizard__success-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #2a5f2a;
  line-height: 1.35;
}

.booking-wizard__success-meta {
  margin: 0;
  font-size: 0.9375rem;
  color: #3d6b3d;
}

.booking-wizard__success-text {
  margin: 0;
  font-size: 0.9375rem;
  color: #3d6b3d;
}

.booking-wizard__success-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #4caf50;
  color: #fff;
  font-size: 20px;
  font-weight: 700;
}

.booking-wizard__captcha-hint {
  margin: 0 0 10px;
  font-size: 13px;
  color: #666;
}

.booking-wizard__apartment-switcher {
  display: flex;
  justify-content: center;
  margin: 0 0 1rem;
}

.booking-wizard__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.booking-wizard__footer-start,
.booking-wizard__footer-end {
  display: flex;
  gap: 12px;
}

.booking-wizard__footer-end {
  margin-left: auto;
}

.booking-wizard__btn {
  width: auto !important;
  min-width: 120px;
  margin: 0 !important;
}

.booking-wizard__summary-card {
  margin-bottom: 20px;
  padding: 18px 20px;
  background: #fafafa;
  border: 1px solid #ececec;
  border-radius: 12px;
}

.booking-wizard__summary-title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 600;
  color: #2f2f2f;
}

.booking-wizard__summary-list {
  margin: 0;
  padding: 0;
}

.booking-wizard__summary-item {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid #e8e8e8;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &--highlight {
    padding-top: 14px;

    dd {
      font-weight: 600;
      color: #2f2f2f;
    }
  }

  dt {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #888;
  }

  dd {
    margin: 0;
    color: #444;
    line-height: 1.5;
  }
}

@media (max-width: 520px) {
  .booking-wizard__summary-item {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
